import classNames from "classnames";
import type { DOMNode, Element, HTMLReactParserOptions } from "html-react-parser";
import parse, { Text, attributesToProps, domToReact } from "html-react-parser";
import React from "react";

import Button from "@atoms/Button";
import Image from "@atoms/Image";
import Link from "@atoms/Link";

const safelist = ["place-self-center", "my-7.5", "items-center", "gap-15", "list-circled-number", "pb-2.5", "pb-5", "indent-6"];

const IMAGE_TAGS = ["img", "picture"];

function containsImage(node: Element): boolean {
  return ((node.children ?? []) as DOMNode[]).some((child) => {
    const element = child as Element;
    return IMAGE_TAGS.includes(element.name) || containsImage(element);
  });
}

function hasText(node: Element): boolean {
  return ((node.children ?? []) as DOMNode[]).some((child) => {
    if (child instanceof Text) return Boolean(child.data.trim());

    const element = child as Element;
    if (!element.name || element.name === "style" || element.name === "script") return false;

    return hasText(element);
  });
}

export function TextContent({ html }: { html: string }) {
  const options: HTMLReactParserOptions = {
    replace: (domNode) => {
      const domParse = domNode as Element;

      if (domParse?.name === "a") {
        // const linkText = domToReact(domParse.children as DOMNode[], options) as React.JSX.Element;
        // return <Link {...attributesToProps(domParse.attribs)}>{linkText}</Link>;
        return <></>;
      }

      // 아티클의 style 블록은 이미지 그리드(.news-grid / .lookbook-grid)만 정의한다.
      // 이미지는 ImageContent 가 앱 레이아웃으로 렌더하므로 텍스트 영역에서는 제거한다.
      if (domParse?.name === "style") {
        return <></>;
      }

      // img와 picture 태그는 텍스트 영역에서 렌더링하지 않음 (제거)
      if (IMAGE_TAGS.includes(domParse?.name)) {
        return <></>;
      }

      // 이미지만 감싸고 있는 래퍼(<section><div class="news-grid">…)는
      // img 만 지우면 빈 껍데기가 남아 여백이 생기므로 통째로 제거한다.
      if (domParse?.name && containsImage(domParse) && !hasText(domParse)) {
        return <></>;
      }

      if (domParse?.name === "p") {
        const paragraphText = domToReact(domParse.children as DOMNode[], options) as React.JSX.Element;
        return <p>{paragraphText}</p>;
      }
    },
  };

  return (
    <>
      {parse(html, options)}
      <div className={classNames("hidden!", safelist)} />
    </>
  );
}

export function ImageContent({ html }: { html: string }) {
  const extractedImages: React.ReactNode[] = [];

  const options: HTMLReactParserOptions = {
    replace: (domNode) => {
      const domParse = domNode as Element;

      // img 태그를 만나면 Image 컴포넌트로 변환하여 배열에 수집
      if (domParse?.name === "img") {
        extractedImages.push(
          <Image
            alt={domParse.attribs.alt || ""}
            className="w-full"
            height={Number(domParse.attribs.height) || 1350}
            key={`${domParse.attribs.src}-${extractedImages.length}`}
            src={domParse.attribs.src}
            width={Number(domParse.attribs.width) || 1080}
          />,
        );

        return <></>;
      }
    },
  };

  parse(html, options);

  return <>{extractedImages}</>;
}

export default function ParseContent({ html, isHorizontal }: { html: string; isHorizontal?: boolean }) {
  const options: HTMLReactParserOptions = {
    replace: (domNode) => {
      const domParse = domNode as Element;

      if (domParse?.name === "iframe" && isHorizontal !== undefined) {
        return (
          <iframe
            {...attributesToProps(domParse.attribs)}
            className={isHorizontal ? "pc:p-4 aspect-video w-full" : "pc:p-4 h-[70%] w-full"}
            height={undefined}
            style={undefined}
            title={domParse.attribs.title || "video"}
            width={undefined}
          />
        );
      }

      if (domParse?.name === "a") {
        const linkText = domToReact(domParse.children as DOMNode[], options) as React.JSX.Element;
        return (
          <Link as="text" {...attributesToProps(domParse.attribs)} className={domParse.attribs.class}>
            {linkText}
          </Link>
        );
      }

      if (domParse?.name === "button") {
        const buttonText = domToReact(domParse.children as DOMNode[], options) as React.JSX.Element;
        return <Button className={domParse?.attribs.class}>{buttonText}</Button>;
      }

      if (domParse.name === "h1" || domParse.name === "h2" || domParse.name === "h3" || domParse.name === "h4") {
        const headingText = domToReact(domParse.children as DOMNode[], options) as React.JSX.Element;
        const ParseDom = domParse.name;
        return <ParseDom className={domParse.attribs.class}>{headingText}</ParseDom>;
      }
    },
  };

  return <>{parse(html, options)}</>;
}
